import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import styled from '@emotion/styled';
import {
  PageContainer,
  Button,
  ErrorAlert,
  LoadingText,
  MutedText,
  InlineCode,
  Stack,
  Row,
  AlertBanner,
} from '../components/styled';
import {
  getPendingConsent,
  submitConsent,
} from '../services/oauthAuthorize';

const Card = styled.div`
  max-width: 560px;
  margin: ${({ theme }) => theme.spacing(12)} auto;
  padding: ${({ theme }) => theme.spacing(8)};
  border: 1px solid ${({ theme }) => theme.border.color.light};
  border-radius: ${({ theme }) => theme.border.radius.md};
  background: ${({ theme }) => theme.background.primary};
`;

const Heading = styled.h1`
  font-size: ${({ theme }) => theme.font.size.xl};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  margin: 0 0 ${({ theme }) => theme.spacing(2)} 0;
  color: ${({ theme }) => theme.font.color.primary};
`;

const ClientName = styled.strong`
  color: ${({ theme }) => theme.font.color.primary};
`;

const ScopeList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const ScopeItem = styled.li`
  padding: ${({ theme }) => theme.spacing(2, 3)};
  border: 1px solid ${({ theme }) => theme.border.color.light};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  font-size: ${({ theme }) => theme.font.size.sm};
`;

const ScopeName = styled.div`
  font-family: monospace;
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.accent.primary};
  margin-bottom: 2px;
`;

const ScopeDescription = styled.div`
  color: ${({ theme }) => theme.font.color.secondary};
`;

function Authorize() {
  const [params] = useSearchParams();
  const handle = params.get('ch') || '';
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['pending-consent', handle],
    queryFn: () => getPendingConsent(handle),
    enabled: !!handle,
    retry: false,
  });

  const consentMutation = useMutation({
    mutationFn: (decision: 'allow' | 'deny') => submitConsent(handle, decision),
    onSuccess: (response) => {
      // Full navigation back to the third-party app's redirect_uri. The
      // browser leaves the dashboard SPA here — that's intentional.
      window.location.href = response.redirectUrl;
    },
  });

  if (!handle) {
    return (
      <PageContainer>
        <Card>
          <ErrorAlert>Missing consent handle. This URL is only valid when reached from the OAuth authorize flow.</ErrorAlert>
        </Card>
      </PageContainer>
    );
  }

  if (isLoading) {
    return <PageContainer><Card><LoadingText>Loading consent details…</LoadingText></Card></PageContainer>;
  }

  if (error || !data) {
    const message = (error as any)?.response?.data?.error_description
      || (error as any)?.response?.data?.error
      || 'This consent request has expired or is invalid. Return to the third-party app and start over.';
    return (
      <PageContainer>
        <Card>
          <ErrorAlert>{message}</ErrorAlert>
        </Card>
      </PageContainer>
    );
  }

  function handleDecision(decision: 'allow' | 'deny') {
    setSubmitting(true);
    consentMutation.mutate(decision, {
      onSettled: () => setSubmitting(false),
    });
  }

  let redirectHost = '';
  try {
    redirectHost = new URL(data.redirectUri).host;
  } catch {
    redirectHost = data.redirectUri;
  }

  return (
    <PageContainer>
      <Card>
        <Stack gap={6}>
          <div>
            <Heading>Authorize {data.client.name}</Heading>
            <MutedText>
              <ClientName>{data.client.name}</ClientName> is requesting access to your Simkura account.
              {data.client.description && <> {data.client.description}</>}
            </MutedText>
          </div>

          <div>
            <MutedText style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
              This app will be able to:
            </MutedText>
            <ScopeList>
              {data.scopes.map((s) => (
                <ScopeItem key={s.name}>
                  <ScopeName>{s.name}</ScopeName>
                  <ScopeDescription>{s.description}</ScopeDescription>
                </ScopeItem>
              ))}
            </ScopeList>
          </div>

          <AlertBanner variant="warning">
            <span>
              After clicking Allow, you'll be sent back to <InlineCode>{redirectHost}</InlineCode>.
              You can revoke this access at any time from Connected Apps.
            </span>
          </AlertBanner>

          {consentMutation.isError && (
            <ErrorAlert>
              Could not record your decision. Please try again from the third-party app.
            </ErrorAlert>
          )}

          <Row gap={3} justify="flex-end">
            <Button
              variant="secondary"
              onClick={() => handleDecision('deny')}
              disabled={submitting || consentMutation.isPending}
            >
              Deny
            </Button>
            <Button
              onClick={() => handleDecision('allow')}
              disabled={submitting || consentMutation.isPending}
            >
              {consentMutation.isPending ? 'Working…' : 'Allow'}
            </Button>
          </Row>
        </Stack>
      </Card>
    </PageContainer>
  );
}

export default Authorize;
